/**
 * html-in-canvas.js
 * ═══════════════════════════════════════════════════════════════════════════
 * Feature detection and utilities for the WICG HTML-in-Canvas specification.
 *
 * The API allows rendering HTML elements directly into <canvas> via
 * drawElementImage() (2D), texElementImage2D (WebGL), or
 * copyElementImageToTexture (WebGPU). Elements participate in layout
 * and hit-testing when the canvas has the `layoutsubtree` attribute.
 *
 * Architecture adapted from marijanapav.com/shop (LensClone):
 *   - Small canvas overlay, not a full-page canvas wrapper
 *   - Page DOM is cloned into a hidden div inside the canvas
 *   - MutationObserver + ResizeObserver keep the clone in sync
 *   - texElementImage2D rasterizes the clone as a GPU texture
 *
 * Spec: https://github.com/WICG/html-in-canvas
 * Flag: chrome://flags/#canvas-draw-element
 *
 * @module html-in-canvas
 * ═══════════════════════════════════════════════════════════════════════════
 */

// ─── Feature Detection ─────────────────────────────────────────────────────

let _supported = null
let _webglSupported = null
const HTML_IN_CANVAS_REFRESH_EVENT = "html-in-canvas:refresh"

export function requestHtmlInCanvasRefresh(options = {}) {
  if (typeof window === "undefined") return

  window.dispatchEvent(
    new CustomEvent(HTML_IN_CANVAS_REFRESH_EVENT, {
      detail: {
        fullSync: options.fullSync === true,
      },
    })
  )
}

/**
 * Checks whether the HTML-in-Canvas API is available by actually
 * exercising it on a throwaway canvas. Prototype sniffing alone can
 * trigger STATUS_BREAKPOINT in builds where the flag is compiled-in
 * but not enabled. A real smoke test is safe-by-default: if anything
 * throws or crashes, we catch it and report "not supported."
 *
 * The test:
 *  1. Creates a tiny offscreen <canvas> with `layoutsubtree`
 *  2. Adds a minimal <span> child
 *  3. Checks for the `onpaint` property
 *  4. Gets a 2D context and checks for `drawElementImage`
 *
 * Result is cached after the first call.
 *
 * @returns {boolean}
 */
export function isHtmlInCanvasSupported() {
  if (_supported !== null) return _supported

  if (typeof window === "undefined" || typeof HTMLCanvasElement === "undefined") {
    _supported = false
    return false
  }

  try {
    const c = document.createElement("canvas")
    c.width = 1
    c.height = 1
    c.setAttribute("layoutsubtree", "")

    // The canvas must support the paint event
    if (!("onpaint" in c)) {
      _supported = false
      return false
    }

    // Check 2D path
    const ctx = c.getContext("2d")
    if (ctx && typeof ctx.drawElementImage === "function") {
      _supported = true
      return true
    }

    // Check WebGL path
    const gl = c.getContext("webgl")
    if (gl && typeof gl.texElementImage2D === "function") {
      _supported = true
      return true
    }

    _supported = false
  } catch (_) {
    _supported = false
  }

  return _supported
}

/**
 * Checks specifically for WebGL HTML-in-Canvas support by testing
 * against a throwaway canvas context.
 *
 * @returns {boolean}
 */
export function isWebGLHtmlInCanvasSupported() {
  if (_webglSupported !== null) return _webglSupported

  if (typeof window === "undefined") {
    _webglSupported = false
    return false
  }

  try {
    const c = document.createElement("canvas")
    c.width = 1
    c.height = 1
    const gl = c.getContext("webgl")
    _webglSupported = !!(gl && typeof gl.texElementImage2D === "function")
  } catch (_) {
    _webglSupported = false
  }

  return _webglSupported
}

// ─── DOM Clone Sync Manager ────────────────────────────────────────────────
//
// Adapted from marijanapav.com/shop (LensClone):
// Clones the page DOM into a hidden <div> inside the canvas, keeps it
// in sync via MutationObserver + ResizeObserver, and provides the source
// size for shader UV mapping.
// ────────────────────────────────────────────────────────────────────────────

/**
 * Build a WeakMap from source nodes to their clones for efficient
 * incremental sync (avoids full re-clone on attribute/text changes).
 */
function buildCloneMappings(sourceRoot, cloneRoot) {
  const sourceToClone = new WeakMap()
  const sourceNodes = [sourceRoot]
  const cloneNodes = [cloneRoot]

  while (sourceNodes.length > 0) {
    const srcNode = sourceNodes.shift()
    const clnNode = cloneNodes.shift()
    if (!clnNode) break
    sourceToClone.set(srcNode, clnNode)
    sourceNodes.push(...Array.from(srcNode.childNodes))
    cloneNodes.push(...Array.from(clnNode.childNodes))
  }

  return sourceToClone
}

function syncElementAttribute(sourceEl, cloneEl, attrName) {
  const value = sourceEl.getAttribute(attrName)
  if (value === null) cloneEl.removeAttribute(attrName)
  else cloneEl.setAttribute(attrName, value)
}

function syncElementAttributes(sourceEl, cloneEl) {
  const cloneAttrs = new Set(cloneEl.getAttributeNames())
  sourceEl.getAttributeNames().forEach(name => {
    syncElementAttribute(sourceEl, cloneEl, name)
    cloneAttrs.delete(name)
  })
  cloneAttrs.forEach(name => cloneEl.removeAttribute(name))
}

function syncImageElementState(sourceImg, cloneImg, renderCallback) {
  const resolvedSrc =
    sourceImg.currentSrc || sourceImg.getAttribute("src") || sourceImg.src || ""
  const sizes = sourceImg.getAttribute("sizes")

  cloneImg.loading = "eager"
  cloneImg.decoding = "sync"
  cloneImg.setAttribute("loading", "eager")
  cloneImg.setAttribute("decoding", "sync")

  if ("fetchPriority" in cloneImg) {
    cloneImg.fetchPriority = "high"
    cloneImg.setAttribute("fetchpriority", "high")
  }

  // Force the clone to use the same resolved resource the source image is
  // already showing. This avoids lazy/offscreen image behavior in the cloned
  // subtree, which otherwise tends to paint as transparent in texElementImage2D.
  if (resolvedSrc) {
    cloneImg.src = resolvedSrc
    cloneImg.setAttribute("src", resolvedSrc)
    cloneImg.removeAttribute("srcset")

    const clonePicture = cloneImg.closest("picture")
    if (clonePicture) {
      clonePicture.querySelectorAll("source").forEach(sourceEl => {
        sourceEl.removeAttribute("srcset")
      })
    }
  }

  if (sizes) cloneImg.setAttribute("sizes", sizes)
  else cloneImg.removeAttribute("sizes")

  const handleMediaReady = () => renderCallback()
  cloneImg.addEventListener("load", handleMediaReady, { once: true })
  cloneImg.addEventListener("error", handleMediaReady, { once: true })

  return () => {
    cloneImg.removeEventListener("load", handleMediaReady)
    cloneImg.removeEventListener("error", handleMediaReady)
  }
}

function syncImageElements(sourceRoot, sourceToClone, renderCallback) {
  const cleanups = []

  if (sourceRoot instanceof HTMLImageElement) {
    const cloneRoot = sourceToClone.get(sourceRoot)
    if (cloneRoot instanceof HTMLImageElement) {
      cleanups.push(syncImageElementState(sourceRoot, cloneRoot, renderCallback))
    }
  }

  sourceRoot.querySelectorAll("img").forEach(sourceImg => {
    const cloneImg = sourceToClone.get(sourceImg)
    if (cloneImg instanceof HTMLImageElement) {
      cleanups.push(syncImageElementState(sourceImg, cloneImg, renderCallback))
    }
  })

  return cleanups
}

/**
 * Sync root-level CSS custom properties and computed styles from the
 * document root to the sourceHost div so the clone renders correctly.
 */
function syncRootStyles(sourceRoot, sourceHost, cloneRoot, prevCustomProps) {
  const rootStyles = window.getComputedStyle(document.documentElement)
  const sourceStyles = window.getComputedStyle(sourceRoot)

  const resolvedBg =
    sourceStyles.backgroundColor !== "rgba(0, 0, 0, 0)"
      ? sourceStyles.backgroundColor
      : rootStyles.backgroundColor

  sourceHost.style.colorScheme = rootStyles.colorScheme
  sourceHost.style.backgroundColor = resolvedBg
  sourceHost.style.color = sourceStyles.color

  // Clear previous custom properties
  prevCustomProps.forEach(prop => sourceHost.style.removeProperty(prop))

  // Copy current custom properties from :root
  const nextCustomProps = []
  for (let i = 0; i < rootStyles.length; i++) {
    const prop = rootStyles[i]
    if (prop.startsWith("--")) {
      nextCustomProps.push(prop)
      sourceHost.style.setProperty(prop, rootStyles.getPropertyValue(prop))
    }
  }

  if (cloneRoot) {
    cloneRoot.style.backgroundColor = resolvedBg
    cloneRoot.style.color = sourceStyles.color
  }

  return nextCustomProps
}

/**
 * Deep-clone the source root into the sourceHost div.
 */
function clonePageIntoSource(sourceRoot, sourceHost) {
  const clone = sourceRoot.cloneNode(true)
  clone.removeAttribute("data-glass-root")
  clone.setAttribute("aria-hidden", "true")
  clone.querySelectorAll("[data-glass-exclude]").forEach(node => node.remove())
  clone.style.margin = "0"
  clone.style.pointerEvents = "none"
  clone.style.width = "100%"
  clone.style.minHeight = "100%"

  const rect = sourceRoot.getBoundingClientRect()
  sourceHost.style.width = `${rect.width}px`
  sourceHost.style.height = `${rect.height}px`
  sourceHost.style.minHeight = `${rect.height}px`
  sourceHost.replaceChildren(clone)

  const sourceToClone = buildCloneMappings(sourceRoot, clone)
  return { clone, sourceToClone, rect }
}

/**
 * Creates a full clone sync manager that:
 *   - Clones the page content into the canvas source host
 *   - Watches for DOM changes via MutationObserver
 *   - Watches for size changes via ResizeObserver
 *   - Watches for theme/style changes on <html>
 *   - Listens for scroll/resize events
 *   - Calls renderCallback whenever the clone is updated
 *
 * @param {HTMLCanvasElement} canvas
 * @param {HTMLElement} sourceHost - Direct child of the canvas (hidden div)
 * @param {string} cloneSelector - CSS selector for the page root to clone
 * @param {() => void} renderCallback - Called to trigger a re-render
 * @returns {{ getSourceSize: () => object, getSourceRoot: () => HTMLElement, cleanup: () => void } | null}
 */
export function createCloneSyncManager(canvas, sourceHost, cloneSelector, renderCallback) {
  const sourceRoot = document.querySelector(cloneSelector)
  if (!sourceRoot) return null

  let cloneRoot = null
  let sourceToClone = new WeakMap()
  let mediaCleanups = []
  let customProps = []
  let fullSyncScheduled = false
  let fullSyncTimeoutId = null
  let renderScheduled = false
  let themeSyncScheduled = false

  const sourceSize = { width: 1, height: 1 }

  function syncSource() {
    mediaCleanups.forEach(cleanup => cleanup())
    mediaCleanups = []

    const result = clonePageIntoSource(sourceRoot, sourceHost)
    cloneRoot = result.clone
    sourceToClone = result.sourceToClone
    sourceSize.width = Math.max(1, result.rect.width)
    sourceSize.height = Math.max(1, result.rect.height)
    customProps = syncRootStyles(sourceRoot, sourceHost, cloneRoot, customProps)
    mediaCleanups = syncImageElements(sourceRoot, sourceToClone, renderCallback)
  }

  function syncSourceMetrics() {
    if (!cloneRoot) return
    const rect = sourceRoot.getBoundingClientRect()
    sourceHost.style.width = `${Math.max(1, rect.width)}px`
    sourceHost.style.height = `${Math.max(1, rect.height)}px`
    sourceHost.style.minHeight = `${Math.max(1, rect.height)}px`
    cloneRoot.style.backgroundColor = sourceHost.style.backgroundColor
    sourceSize.width = Math.max(1, rect.width)
    sourceSize.height = Math.max(1, rect.height)
  }

  function scheduleFullSync(delayMs = 80) {
    if (fullSyncTimeoutId !== null) window.clearTimeout(fullSyncTimeoutId)
    fullSyncScheduled = true
    fullSyncTimeoutId = window.setTimeout(() => {
      fullSyncScheduled = false
      fullSyncTimeoutId = null
      syncSource()
      renderCallback()
    }, delayMs)
  }

  function scheduleRender() {
    if (fullSyncScheduled || renderScheduled) return
    renderScheduled = true
    window.requestAnimationFrame(() => {
      renderScheduled = false
      syncSourceMetrics()
      renderCallback()
    })
  }

  function scheduleThemeSync() {
    if (fullSyncScheduled || themeSyncScheduled) return
    themeSyncScheduled = true
    window.requestAnimationFrame(() => {
      themeSyncScheduled = false
      if (!cloneRoot) {
        scheduleFullSync(0)
        return
      }
      customProps = syncRootStyles(sourceRoot, sourceHost, cloneRoot, customProps)
      renderCallback()
    })
  }

  // Initial clone
  syncSource()

  // Watch source root for size changes
  const resizeObserver = new ResizeObserver(() => scheduleFullSync(0))
  resizeObserver.observe(sourceRoot)

  // Watch source root for DOM changes
  const mutationObserver = new MutationObserver(records => {
    let needsFullSync = false

    records.forEach(record => {
      if (needsFullSync) return

      if (record.type === "childList") {
        needsFullSync = true
        return
      }

      if (record.type === "characterData") {
        const cloneText = sourceToClone.get(record.target)
        if (cloneText) {
          cloneText.textContent = record.target.textContent
          return
        }
        needsFullSync = true
        return
      }

      if (record.type === "attributes") {
        const srcEl = record.target
        const clnEl = sourceToClone.get(srcEl)
        if (!(clnEl instanceof Element)) {
          needsFullSync = true
          return
        }
        if (record.attributeName) {
          syncElementAttribute(srcEl, clnEl, record.attributeName)
        } else {
          syncElementAttributes(srcEl, clnEl)
        }

        if (srcEl instanceof HTMLImageElement && clnEl instanceof HTMLImageElement) {
          syncImageElementState(srcEl, clnEl, renderCallback)
        }
      }
    })

    if (needsFullSync) {
      scheduleFullSync()
      return
    }
    scheduleRender()
  })

  mutationObserver.observe(sourceRoot, {
    attributes: true,
    childList: true,
    subtree: true,
    characterData: true,
  })

  // Watch <html> for theme/style changes
  const rootObserver = new MutationObserver(() => scheduleThemeSync())
  rootObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["class", "style"],
  })

  const handleImageStateChange = event => {
    const sourceImg = event.target
    if (!(sourceImg instanceof HTMLImageElement)) return

    const cloneImg = sourceToClone.get(sourceImg)
    if (cloneImg instanceof HTMLImageElement) {
      syncImageElementState(sourceImg, cloneImg, renderCallback)
    }
    scheduleRender()
  }

  sourceRoot.addEventListener("load", handleImageStateChange, true)
  sourceRoot.addEventListener("error", handleImageStateChange, true)

  const handleExternalRefresh = event => {
    if (event?.detail?.fullSync) {
      scheduleFullSync(0)
      return
    }
    scheduleRender()
  }

  // Window events
  const handleResize = () => scheduleFullSync(0)
  const handleScroll = () => scheduleRender()
  window.addEventListener(HTML_IN_CANVAS_REFRESH_EVENT, handleExternalRefresh)
  window.addEventListener("resize", handleResize)
  window.addEventListener("scroll", handleScroll, { passive: true })

  return {
    getSourceSize() {
      return sourceSize
    },
    getSourceRoot() {
      return sourceRoot
    },
    cleanup() {
      mediaCleanups.forEach(cleanup => cleanup())
      resizeObserver.disconnect()
      mutationObserver.disconnect()
      rootObserver.disconnect()
      sourceRoot.removeEventListener("load", handleImageStateChange, true)
      sourceRoot.removeEventListener("error", handleImageStateChange, true)
      window.removeEventListener(HTML_IN_CANVAS_REFRESH_EVENT, handleExternalRefresh)
      window.removeEventListener("resize", handleResize)
      window.removeEventListener("scroll", handleScroll)
      if (fullSyncTimeoutId !== null) window.clearTimeout(fullSyncTimeoutId)
      sourceHost.replaceChildren()
    },
  }
}
